import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
// @ts-ignore - frappe-gantt doesn't have TypeScript declarations
import Gantt from 'frappe-gantt';
import { getProjectRequirements, RequirementWithDetails, updateRequirement } from '../services/requirementService';
import '../frappe-gantt.css';

interface PendingChange {
  requirementId: string;
  requirementTitle: string;
  oldStart: string;
  oldEnd: string;
  newStart: string;
  newEnd: string;
}

interface GanttTask {
  id: string;
  name: string;
  start: string;
  end: string;
  progress: number;
  dependencies?: string;
  custom_class?: string;
}

/**
 * GanttChart Component
 *
 * Displays a visual Gantt chart timeline view of all project requirements
 * based on their expected start and end dates.
 */
const GanttChart: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const ganttContainerRef = useRef<HTMLDivElement>(null);
  const ganttInstanceRef = useRef<Gantt | null>(null);

  const [requirements, setRequirements] = useState<RequirementWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'Day' | 'Week' | 'Month' | 'Year'>('Week');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [draggedRequirement, setDraggedRequirement] = useState<RequirementWithDetails | null>(null);
  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);

  // Fetch requirements
  useEffect(() => {
    const fetchRequirements = async () => {
      if (!projectId || !token) return;

      try {
        setLoading(true);
        setError(null);
        const data = await getProjectRequirements(projectId, token);
        setRequirements(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load requirements');
      } finally {
        setLoading(false);
      }
    };

    fetchRequirements();
  }, [projectId, token]);

  // Calculate progress percentage based on status
  const calculateProgress = (req: RequirementWithDetails): number => {
    switch (req.status) {
      case 'completed':
        return 100;
      case 'in-progress':
        return 50;
      case 'approved':
        return 25;
      case 'draft':
      case 'rejected':
      case 'deferred':
        return 0;
      default:
        return 0;
    }
  };

  // Get custom class for styling based on requirement type and status
  // Note: frappe-gantt adds "bar-" prefix to these classes
  const getCustomClass = (req: RequirementWithDetails): string => {
    const classes: string[] = [];

    // Requirement type classes (frappe-gantt will apply as .bar-functional, .bar-non-functional, etc.)
    if (req.requirement_type === 'functional') classes.push('functional');
    if (req.requirement_type === 'non-functional') classes.push('non-functional');
    if (req.requirement_type === 'technical') classes.push('technical');
    if (req.requirement_type === 'business') classes.push('business');
    if (req.requirement_type === 'user-story') classes.push('user-story');

    // Status classes (frappe-gantt will apply as .bar-completed, .bar-overdue)
    if (req.status === 'completed') classes.push('completed');
    if (req.is_overdue) classes.push('overdue');

    return classes.join(' ');
  };

  // Get all dependencies (both hierarchical parent and workflow dependencies)
  const getAllDependencies = (req: RequirementWithDetails): string | undefined => {
    const dependencies: string[] = [];

    // Add parent dependency if it's a child requirement (hierarchical)
    if (req.hierarchy_type === 'child' && req.parent_requirement_id) {
      dependencies.push(req.parent_requirement_id);
    }

    // Add workflow dependencies from backend (comma-separated string)
    if (req.dependencies) {
      dependencies.push(req.dependencies);
    }

    // Return comma-separated string or undefined if no dependencies
    return dependencies.length > 0 ? dependencies.join(',') : undefined;
  };

  // Transform requirements to Gantt tasks
  const transformRequirementsToGanttTasks = (): GanttTask[] => {
    let filteredReqs = requirements.filter(
      req => req.expected_start_date && req.expected_end_date
    );

    // Apply status filter
    if (filterStatus !== 'all') {
      filteredReqs = filteredReqs.filter(req => req.status === filterStatus);
    }

    // Apply priority filter
    if (filterPriority !== 'all') {
      filteredReqs = filteredReqs.filter(req => req.priority === filterPriority);
    }

    // Apply type filter
    if (filterType !== 'all') {
      filteredReqs = filteredReqs.filter(req => req.requirement_type === filterType);
    }

    return filteredReqs.map(req => ({
      id: req.id,
      name: req.title,
      start: req.expected_start_date!.split('T')[0],
      end: req.expected_end_date!.split('T')[0],
      progress: calculateProgress(req),
      custom_class: getCustomClass(req),
      dependencies: getAllDependencies(req)
    }));
  };

  // Render Gantt chart
  useEffect(() => {
    if (!ganttContainerRef.current) {
      console.log('Gantt container ref not available');
      return;
    }

    const tasks = transformRequirementsToGanttTasks();
    console.log('Gantt tasks to render:', tasks.length);

    if (tasks.length === 0) {
      // Clear any existing chart when there are no tasks
      if (ganttInstanceRef.current) {
        ganttContainerRef.current.innerHTML = '';
        ganttInstanceRef.current = null;
      }
      return;
    }

    // Clear existing chart
    if (ganttContainerRef.current) {
      ganttContainerRef.current.innerHTML = '';
    }
    ganttInstanceRef.current = null;

    // Delay chart creation slightly to ensure DOM is ready
    const timer = setTimeout(() => {
      if (!ganttContainerRef.current) {
        console.error('Gantt container disappeared');
        return;
      }

      // Create new Gantt chart
      try {
        console.log('Creating Gantt chart in container:', ganttContainerRef.current);

        // Calculate desired height for the Gantt chart
        const desiredHeight = Math.max(600, window.innerHeight - 480);

        ganttInstanceRef.current = new Gantt(ganttContainerRef.current, tasks, {
          view_mode: viewMode,
          bar_height: 30,
          bar_corner_radius: 3,
          padding: 18,
          on_click: (task: any) => {
            // Navigate to requirement detail or open modal
            console.log('Clicked task:', task);
          },
          on_date_change: (task: any, start: Date, end: Date) => {
            // Track pending date changes
            const requirementId = task.id;
            const requirement = requirements.find(r => r.id === requirementId);

            if (requirement) {
              const newChange: PendingChange = {
                requirementId: requirement.id,
                requirementTitle: requirement.title,
                oldStart: requirement.expected_start_date || '',
                oldEnd: requirement.expected_end_date || '',
                newStart: start.toISOString().split('T')[0],
                newEnd: end.toISOString().split('T')[0]
              };

              setPendingChanges(prev => {
                const updated = new Map(prev);
                updated.set(requirementId, newChange);
                return updated;
              });

              console.log('Date change tracked:', newChange);
            }
          },
          on_progress_change: (task: any, progress: number) => {
            // Handle progress changes (future feature)
            console.log('Progress changed:', task, progress);
          },
          on_view_change: (mode: string) => {
            console.log('View mode changed:', mode);
          }
        });
        console.log('Gantt chart created successfully');

        // Force the SVG to extend to the full height
        setTimeout(() => {
          if (ganttContainerRef.current) {
            const svg = ganttContainerRef.current.querySelector('svg.gantt');
            if (svg) {
              svg.setAttribute('height', desiredHeight.toString());

              // Also extend the grid background
              const gridBackground = svg.querySelector('.grid-background');
              if (gridBackground) {
                gridBackground.setAttribute('height', desiredHeight.toString());
              }

              // Extend the grid rows
              const gridRows = svg.querySelectorAll('.grid-row');
              gridRows.forEach((row) => {
                row.setAttribute('height', desiredHeight.toString());
              });

              // Extend the today highlight
              const todayHighlight = svg.querySelector('.today-highlight');
              if (todayHighlight) {
                todayHighlight.setAttribute('height', desiredHeight.toString());
              }

              // Apply dynamic label coloring based on position and add hover listeners
              // Labels inside bars should be white, labels outside should be black
              const barWrappers = svg.querySelectorAll('.bar-wrapper');
              barWrappers.forEach((wrapper) => {
                const bar = wrapper.querySelector('.bar');
                const label = wrapper.querySelector('.bar-label');

                if (bar && label) {
                  const barRect = (bar as SVGGraphicsElement).getBBox();
                  const labelRect = (label as SVGGraphicsElement).getBBox();

                  // Check if label's center is inside the bar
                  const labelCenterX = labelRect.x + labelRect.width / 2;
                  const isInside = labelCenterX >= barRect.x && labelCenterX <= (barRect.x + barRect.width);

                  // Apply color based on position
                  if (isInside) {
                    (label as SVGElement).style.fill = 'white';
                  } else {
                    (label as SVGElement).style.fill = '#333';
                  }
                }

                // Add hover event listeners to show/hide dependency arrows
                const taskId = wrapper.getAttribute('data-id');

                wrapper.addEventListener('mouseenter', () => {
                  if (taskId) {
                    setHoveredTaskId(taskId);
                  }
                });

                wrapper.addEventListener('mouseleave', () => {
                  setHoveredTaskId(null);
                });
              });

              // Style the "Today" button in frappe-gantt header
              const allTextElements = svg.querySelectorAll('text');
              allTextElements.forEach((textEl) => {
                if (textEl.textContent?.trim() === 'Today') {
                  // Found the Today text, now find and style its background rect
                  const parentG = textEl.parentElement;
                  if (parentG) {
                    // Style the text
                    (textEl as SVGElement).style.fill = 'white';
                    (textEl as SVGElement).style.fontWeight = '600';

                    // Find the rect that's the background (usually a sibling or in parent)
                    const rect = parentG.querySelector('rect');
                    if (rect) {
                      (rect as SVGElement).style.fill = '#4f46e5';
                      (rect as SVGElement).setAttribute('rx', '12');
                      (rect as SVGElement).setAttribute('ry', '12');
                      (rect as SVGElement).style.cursor = 'pointer';
                    }
                  }
                }
              });
            }
          }
        }, 200);
      } catch (err) {
        console.error('Error rendering Gantt chart:', err);
        setError('Failed to render Gantt chart');
      }
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [requirements, viewMode, filterStatus, filterPriority, filterType]);

  // Effect to manage dependency arrow visibility based on hover
  useEffect(() => {
    if (!ganttContainerRef.current) return;

    const svg = ganttContainerRef.current.querySelector('svg.gantt');
    if (!svg) return;

    const arrows = svg.querySelectorAll('.arrow');

    if (!hoveredTaskId) {
      // Hide all arrows when no task is hovered
      arrows.forEach(arrow => {
        (arrow as SVGElement).style.display = 'none';
      });
    } else {
      // Show only arrows related to the hovered task
      arrows.forEach(arrow => {
        const fromTaskId = arrow.getAttribute('data-from');
        const toTaskId = arrow.getAttribute('data-to');

        // Show arrow if it's connected to the hovered task (either from or to)
        if (fromTaskId === hoveredTaskId || toTaskId === hoveredTaskId) {
          (arrow as SVGElement).style.display = 'block';
        } else {
          (arrow as SVGElement).style.display = 'none';
        }
      });
    }
  }, [hoveredTaskId]);

  // Apply all filters to requirements
  const getFilteredRequirements = (reqs: RequirementWithDetails[]) => {
    let filtered = reqs;

    console.log('Filter values:', { filterStatus, filterPriority, filterType });
    console.log('Total requirements before filtering:', filtered.length);

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(req => req.status === filterStatus);
      console.log('After status filter:', filtered.length);
    }

    // Apply priority filter
    if (filterPriority !== 'all') {
      filtered = filtered.filter(req => req.priority === filterPriority);
      console.log('After priority filter:', filtered.length);
    }

    // Apply type filter
    if (filterType !== 'all') {
      console.log('Filtering by type:', filterType);
      console.log('Sample requirement types:', filtered.slice(0, 3).map(r => ({ title: r.title, type: r.requirement_type })));
      filtered = filtered.filter(req => req.requirement_type === filterType);
      console.log('After type filter:', filtered.length);
    }

    return filtered;
  };

  const requirementsWithDates = getFilteredRequirements(
    requirements.filter(req => req.expected_start_date && req.expected_end_date)
  );

  const requirementsWithoutDates = getFilteredRequirements(
    requirements.filter(req => !req.expected_start_date || !req.expected_end_date)
  );

  // Handle committing all pending changes
  const handleCommitChanges = async () => {
    if (!projectId || !token || pendingChanges.size === 0) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    const changes = Array.from(pendingChanges.values());
    const results = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    try {
      // Update each requirement with its new dates
      for (const change of changes) {
        try {
          console.log('Updating requirement:', change);
          await updateRequirement(
            projectId,
            change.requirementId,
            {
              expected_start_date: change.newStart,
              expected_end_date: change.newEnd
            },
            token
          );
          results.success++;
          console.log('Successfully updated requirement:', change.requirementId);
        } catch (err: any) {
          console.error('Failed to update requirement:', change.requirementId, err);
          console.error('Error response:', err.response?.data);
          results.failed++;
          const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
          results.errors.push(`${change.requirementTitle}: ${errorMsg}`);
        }
      }

      // Show results
      if (results.failed === 0) {
        setSuccessMessage(`Successfully updated ${results.success} requirement${results.success > 1 ? 's' : ''}`);
        setPendingChanges(new Map());

        // Reload requirements to get fresh data
        const data = await getProjectRequirements(projectId, token);
        setRequirements(data);
      } else {
        setError(
          `Updated ${results.success} requirement(s), but ${results.failed} failed:\n${results.errors.join('\n')}`
        );
      }
    } catch (err: any) {
      setError(`Failed to commit changes: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle discarding all pending changes
  const handleDiscardChanges = () => {
    setPendingChanges(new Map());
    setSuccessMessage('All pending changes discarded');

    // Clear the success message after 3 seconds
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Get priority badge color
  const getPriorityColor = (priority: string): string => {
    const colors: Record<string, string> = {
      critical: '#fecaca; color: #991b1b',
      high: '#fed7aa; color: #9a3412',
      medium: '#fef08a; color: #854d0e',
      low: '#d9f99d; color: #365314'
    };
    return colors[priority] || '#e5e7eb; color: #374151';
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, req: RequirementWithDetails) => {
    setDraggedRequirement(req);

    // Create a styled drag preview that looks like the requirement card
    const dragPreview = document.createElement('div');
    dragPreview.style.cssText = `
      position: absolute;
      top: -1000px;
      left: -1000px;
      width: 280px;
      padding: 12px;
      background: white;
      border: 2px solid #6366f1;
      border-radius: 6px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      pointer-events: none;
      z-index: 10000;
    `;

    // Add content to the drag preview
    dragPreview.innerHTML = `
      <div style="font-size: 14px; font-weight: 600; color: #111827; margin-bottom: 4px; line-height: 1.4;">
        ${req.title}
      </div>
      <div style="display: flex; gap: 6px; flex-wrap: wrap;">
        <span style="display: inline-block; padding: 2px 8px; border-radius: 9999px; font-size: 11px; font-weight: 500; background-color: ${getPriorityColor(req.priority)};">
          ${req.priority}
        </span>
        <span style="display: inline-block; padding: 2px 8px; border-radius: 9999px; font-size: 11px; font-weight: 500; background-color: #e0e7ff; color: #4338ca;">
          ${req.requirement_type}
        </span>
      </div>
    `;

    document.body.appendChild(dragPreview);

    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', req.id);

    try {
      e.dataTransfer.setDragImage(dragPreview, 140, 30);
      // Remove the preview element after a short delay
      setTimeout(() => {
        document.body.removeChild(dragPreview);
      }, 0);
    } catch (err) {
      console.error('Error setting drag image:', err);
      document.body.removeChild(dragPreview);
    }
  };

  const handleDragEnd = () => {
    setDraggedRequirement(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedRequirement) return;

    console.log('Dropping requirement:', draggedRequirement);

    // Calculate default dates: start today, end in 7 days
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 7);

    const newChange: PendingChange = {
      requirementId: draggedRequirement.id,
      requirementTitle: draggedRequirement.title,
      oldStart: draggedRequirement.expected_start_date || '',
      oldEnd: draggedRequirement.expected_end_date || '',
      newStart: today.toISOString().split('T')[0],
      newEnd: endDate.toISOString().split('T')[0]
    };

    console.log('New pending change:', newChange);

    setPendingChanges(prev => {
      const updated = new Map(prev);
      updated.set(draggedRequirement.id, newChange);
      return updated;
    });

    setDraggedRequirement(null);
    setSuccessMessage(`"${draggedRequirement.title}" scheduled for ${newChange.newStart} to ${newChange.newEnd}. Click "Commit Changes" to save.`);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading Gantt chart...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg mb-6 p-6">
          <div className="flex justify-between items-center">
            <div>
              <button
                onClick={() => navigate(`/projects/${projectId}`)}
                className="text-sm text-blue-100 hover:text-white mb-2 inline-flex items-center transition-colors"
              >
                ← Back to Project
              </button>
              <h1 className="text-3xl font-bold text-white">
                Requirements Timeline
              </h1>
              <p className="text-blue-100 mt-1">
                Gantt chart view of project requirements schedule
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 rounded-md bg-red-50 border border-red-200">
            <p className="text-sm text-red-800 whitespace-pre-line">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 rounded-md bg-green-50 border border-green-200">
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Pending Changes Banner */}
        {pendingChanges.size > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow-lg border-2 border-yellow-400 p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    className="h-5 w-5 text-yellow-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Pending Changes ({pendingChanges.size})
                  </h3>
                </div>
                <div className="space-y-2">
                  {Array.from(pendingChanges.values()).map((change) => (
                    <div key={change.requirementId} className="text-sm text-gray-700 pl-7">
                      <span className="font-medium">{change.requirementTitle}</span>
                      <br />
                      <span className="text-gray-500">
                        {change.oldStart} → {change.newStart} | {change.oldEnd} → {change.newEnd}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 ml-4">
                <button
                  onClick={handleDiscardChanges}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Discard
                </button>
                <button
                  onClick={handleCommitChanges}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Commit Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Controls */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* View Mode Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">View Mode:</label>
              <div className="flex gap-2">
                {(['Day', 'Week', 'Month', 'Year'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      viewMode === mode
                        ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="approved">Approved</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
                <option value="deferred">Deferred</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Priority:</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Type:</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Types</option>
                <option value="functional">Functional</option>
                <option value="non-functional">Non-Functional</option>
                <option value="technical">Technical</option>
                <option value="business">Business</option>
                <option value="user-story">User Story</option>
              </select>
            </div>

            {/* Statistics */}
            <div className="ml-auto flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-700">{requirementsWithDates.length}</span>
                <span className="text-gray-500">with dates</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-700">{requirementsWithoutDates.length}</span>
                <span className="text-gray-500">without dates</span>
              </div>
            </div>
          </div>
        </div>

        {/* Gantt Chart with Sidebar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="flex">
            {/* Left Sidebar - Requirements List */}
            <div className="w-80 border-r border-gray-200 overflow-y-auto bg-gray-50" style={{ maxHeight: '600px' }}>
              <div className="p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
                <h3 className="text-sm font-semibold text-gray-900">All Requirements</h3>
                <p className="text-xs text-gray-500 mt-1">{requirementsWithDates.length + requirementsWithoutDates.length} total</p>
              </div>
              <div className="p-2">
                {(requirementsWithDates.length + requirementsWithoutDates.length) === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-500">
                    No requirements found
                  </div>
                ) : (
                  [...requirementsWithDates, ...requirementsWithoutDates].map((req) => {
                    const hasSchedule = req.expected_start_date && req.expected_end_date;
                    return (
                      <div
                        key={req.id}
                        draggable={!hasSchedule}
                        onDragStart={(e) => !hasSchedule && handleDragStart(e, req)}
                        onDragEnd={handleDragEnd}
                        className={`mb-2 p-3 rounded-md border transition-colors ${
                          hasSchedule
                            ? 'bg-white border-gray-200 hover:border-indigo-300'
                            : 'bg-gray-100 border-gray-200 hover:border-gray-300 cursor-move'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {/* Priority Indicator */}
                          <div className={`w-1 h-full rounded-full flex-shrink-0 ${
                            req.priority === 'critical' ? 'bg-red-500' :
                            req.priority === 'high' ? 'bg-orange-500' :
                            req.priority === 'medium' ? 'bg-blue-500' :
                            'bg-green-500'
                          }`}></div>

                          <div className="flex-1 min-w-0">
                            {/* Title */}
                            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
                              {req.title}
                            </h4>

                            {/* Meta Info */}
                            {req.assigned_to_name && (
                              <div className="mt-1 text-xs text-gray-600">
                                {req.assigned_to_name}
                              </div>
                            )}

                            {/* Schedule Info or Warning */}
                            {hasSchedule ? (
                              <div className="mt-2 text-xs text-gray-600">
                                {new Date(req.expected_start_date!).toLocaleDateString()} - {new Date(req.expected_end_date!).toLocaleDateString()}
                              </div>
                            ) : (
                              <div className="mt-2 flex items-center gap-1 text-xs text-amber-600">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <span>Not scheduled</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right Side - Gantt Chart */}
            <div
              className="flex-1 overflow-x-auto p-6 bg-white relative"
              style={{ minWidth: '600px', minHeight: 'calc(100vh - 420px)' }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              {draggedRequirement && (
                <div className="absolute inset-0 bg-indigo-50 bg-opacity-50 border-4 border-dashed border-indigo-400 flex items-center justify-center z-10 pointer-events-none">
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <p className="mt-2 text-lg font-semibold text-indigo-900">Drop to schedule</p>
                    <p className="text-sm text-indigo-700">Will create a 7-day task starting today</p>
                  </div>
                </div>
              )}
              {requirementsWithDates.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No scheduled requirements</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Requirements need expected start and end dates to appear on the timeline.
                    </p>
                    <p className="mt-2 text-xs text-gray-400">
                      Edit requirements to add dates and they will appear here.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="gantt-chart-wrapper" style={{ minWidth: '800px', minHeight: 'calc(100vh - 480px)' }}>
                  <div ref={ganttContainerRef}></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Legend */}
        {requirementsWithDates.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Legend</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 h-3 rounded gantt-functional"></div>
                <span className="text-gray-700">Functional</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-3 rounded gantt-non-functional"></div>
                <span className="text-gray-700">Non-Functional</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-3 rounded gantt-technical"></div>
                <span className="text-gray-700">Technical</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-3 rounded gantt-business"></div>
                <span className="text-gray-700">Business</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-3 rounded gantt-user-story"></div>
                <span className="text-gray-700">User Story</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom Styles */}
      <style>{`
        .gantt-chart-wrapper {
          overflow-x: auto;
          display: flex;
          flex-direction: column;
        }

        .gantt-chart-wrapper > div {
          flex: 1;
          min-height: 100%;
        }

        .gantt-chart-wrapper svg.gantt {
          min-height: 100% !important;
        }

        /* Hide dependency arrows by default - shown only on hover */
        .gantt .arrow {
          display: none !important;
        }

        /* Frappe-gantt bar colors - muted with opacity for better text visibility */
        .gantt .bar-wrapper.functional .bar,
        .gantt .bar-wrapper.functional rect {
          fill: #3b82f6 !important;
          fill-opacity: 0.7 !important;
        }

        .gantt .bar-wrapper.non-functional .bar,
        .gantt .bar-wrapper.non-functional rect {
          fill: #8b5cf6 !important;
          fill-opacity: 0.7 !important;
        }

        .gantt .bar-wrapper.technical .bar,
        .gantt .bar-wrapper.technical rect {
          fill: #06b6d4 !important;
          fill-opacity: 0.7 !important;
        }

        .gantt .bar-wrapper.business .bar,
        .gantt .bar-wrapper.business rect {
          fill: #f59e0b !important;
          fill-opacity: 0.7 !important;
        }

        .gantt .bar-wrapper.user-story .bar,
        .gantt .bar-wrapper.user-story rect {
          fill: #10b981 !important;
          fill-opacity: 0.7 !important;
        }

        .gantt .bar-wrapper.completed .bar,
        .gantt .bar-wrapper.completed rect {
          fill: #059669 !important;
          fill-opacity: 0.7 !important;
        }

        .gantt .bar-wrapper.overdue .bar,
        .gantt .bar-wrapper.overdue rect {
          fill: #dc2626 !important;
          fill-opacity: 0.7 !important;
        }

        /* Bar label text - bold styling and color dynamically set by JavaScript */
        .gantt .bar-wrapper .bar-label {
          font-weight: bold;
        }

        .gantt-functional {
          background-color: #3b82f6;
        }

        .gantt-non-functional {
          background-color: #8b5cf6;
        }

        .gantt-technical {
          background-color: #06b6d4;
        }

        .gantt-business {
          background-color: #f59e0b;
        }

        .gantt-user-story {
          background-color: #10b981;
        }

        .gantt-completed {
          background-color: #059669;
        }

        .gantt-overdue {
          background-color: #dc2626;
        }

        /* Style the frappe-gantt "Today" button to match selected view mode button */
        .gantt .upper-text,
        svg.gantt .upper-text,
        .gantt-container .upper-text {
          fill: white !important;
          font-weight: 600 !important;
        }

        /* Style the Today button background rectangle */
        .gantt .upper-text + rect,
        svg.gantt .upper-text + rect,
        .gantt-container .upper-text + rect,
        .gantt rect[fill="#f0f0f0"],
        svg.gantt rect[fill="#f0f0f0"] {
          fill: url(#todayButtonGradient) !important;
        }

        /* Create gradient definition */
        svg.gantt defs,
        .gantt defs {
          /* Gradient will be added via JavaScript */
        }

        /* Alternative approach - style any text containing "Today" */
        .gantt text:has-text("Today"),
        svg.gantt text {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default GanttChart;
